// Centered "delete property" confirmation dialog.
//
// Used on both mobile and desktop. Triggers the existing `useDeleteProperty()`
// mutation; navigates back to /properties on success.
//
// Type-to-confirm: the destructive button stays disabled until the user
// retypes the property's internal code. The aggregate-count queries
// (images / activities / appointments) live in an inner component that only
// mounts when `open` is true.
//
// Design source: playground/delete-property-modal-desktop.html

import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { AuthImg } from "@/components/auth-img";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { localizeError } from "@/lib/errorMessages";
import {
  useDeleteProperty,
  usePropertyImages,
} from "@/features/property/api";
import { useAppointments } from "@/features/appointment/api";
import { useActivities } from "@/features/activity/api";
import { formatPrice } from "@/features/property/utils/format-price";
import type { Property } from "@/types/property";

interface DeletePropertyConfirmDialogProps {
  property: Property;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

export function DeletePropertyConfirmDialog({
  property,
  open,
  onOpenChange,
}: DeletePropertyConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onOpenChange(false);
      }}
    >
      {open ? (
        <DialogBody property={property} onClose={() => onOpenChange(false)} />
      ) : null}
    </Dialog>
  );
}

function DialogBody({
  property,
  onClose,
}: {
  property: Property;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const deleteMutation = useDeleteProperty();
  const [typed, setTyped] = useState("");

  // Aggregate counts. All three queries fire only while the dialog is mounted
  // (the body is rendered conditionally above), and React Query dedupes against
  // keys the parent page already issued (e.g. the images list).
  const imagesQuery = usePropertyImages(property.id);
  const activitiesQuery = useActivities({
    entityType: "PROPERTY",
    entityId: property.id,
    page: 0,
    size: 1,
  });
  // `useAppointments` is not pageable and has no propertyId filter; pull the
  // full list and filter client-side. Cheap on a small org.
  const appointmentsQuery = useAppointments();

  const imageCount = imagesQuery.data?.length ?? 0;
  const totalActivities = activitiesQuery.data?.totalItems ?? 0;
  const activeAppointmentCount = useMemo(() => {
    const all = appointmentsQuery.data ?? [];
    return all.filter(
      (a) => a.propertyId === property.id && a.status !== "CANCELED",
    ).length;
  }, [appointmentsQuery.data, property.id]);

  const expectedCode = property.internalCode ?? "";
  // A property with no internal code has nothing to retype — the destructive
  // button inside this confirm dialog is safety enough. Only gate on
  // type-to-confirm when there IS a code; otherwise the delete could never be
  // enabled (there'd be no input to type into).
  const requiresTyping = expectedCode.trim().length > 0;
  const isConfirmed =
    !requiresTyping || typed.trim() === expectedCode.trim();

  const isPending = deleteMutation.isPending;

  const handleConfirm = async () => {
    if (!isConfirmed || isPending) return;
    try {
      await deleteMutation.mutateAsync(property.id);
      toast.success("Đã xoá tin đăng");
      onClose();
      navigate("/properties");
    } catch (error) {
      toast.error(localizeError(error));
    }
  };

  const buildingName = property.buildingName ?? "Bất động sản";
  const codeLabel = property.internalCode ?? `#${property.id}`;
  const priceLabel = formatPrice(property.price, property.listingType);
  const summaryMeta = [codeLabel, property.wardDisplayName, priceLabel]
    .filter((s) => s != null && s !== "")
    .join(" · ");

  return (
    <DialogContent
      showCloseButton={false}
      className="gap-0 overflow-hidden p-0 sm:max-w-[460px]"
    >
      <DialogTitle className="sr-only">Xoá tin đăng</DialogTitle>

      {/* Header — crest + label + question, left-aligned */}
      <div className="flex items-start gap-3 px-5 pt-5 pb-3">
        <span
          aria-hidden
          className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600"
        >
          <X aria-hidden="true" className="size-5" />
        </span>
        <div className="flex flex-col gap-0.5 pt-0.5">
          <span className="font-mono text-[11px] font-bold tracking-[0.08em] text-destructive uppercase">
            Xoá tin đăng
          </span>
          <h2 className="text-[16px] leading-snug font-semibold">
            Bạn chắc chắn muốn xoá tin này?
          </h2>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 px-5 pb-4">
        <p className="text-[12px] leading-relaxed text-ink-soft">
          Tin sẽ bị xoá vĩnh viễn — bao gồm ảnh, mô tả, lịch sử thay đổi và link
          công khai. Hành động này{" "}
          <span className="font-bold text-foreground">không thể hoàn tác.</span>
        </p>

        {/* Property summary card */}
        <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-2.5">
          <div className="size-11 shrink-0 overflow-hidden rounded-lg bg-muted">
            <AuthImg
              src={property.thumbnailUrl ?? ""}
              alt=""
              className="size-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <div className="truncate text-[14px] font-semibold leading-tight">
              {buildingName}
            </div>
            <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
              {summaryMeta}
            </div>
          </div>
        </div>

        {/* SẼ MẤT box */}
        <LossList
          imageCount={imageCount}
          totalActivities={totalActivities}
          activeAppointmentCount={activeAppointmentCount}
        />

        {/* Type-to-confirm input — only when the property has a code to retype. */}
        {requiresTyping ? (
          <div className="mt-0.5 flex flex-col gap-1.5">
            <label htmlFor="confirm-code" className="text-[12px] text-ink-soft">
              Nhập{" "}
              <span className="font-mono font-semibold text-destructive">
                {expectedCode}
              </span>{" "}
              để xác nhận
            </label>
            <div className="relative">
              <Input
                id="confirm-code"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={expectedCode}
                autoComplete="off"
                spellCheck={false}
                className="font-mono tracking-wide"
              />
              {isConfirmed ? (
                <Check
                  aria-hidden="true"
                  className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-emerald-600"
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-5 py-3">
        <span className="text-[11px] text-ink-faint">
          <kbd className="font-mono">ESC</kbd> để huỷ
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={onClose}
            className="h-9"
          >
            Huỷ
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!isConfirmed || isPending}
            onClick={() => void handleConfirm()}
            className="h-9"
          >
            {isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <X aria-hidden="true" className="size-4" />
            )}
            Xoá vĩnh viễn
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

// ---------------------------------------------------------------------------
// Internal: rose-tinted "SẼ MẤT" (will be lost) box
// ---------------------------------------------------------------------------

function LossList({
  imageCount,
  totalActivities,
  activeAppointmentCount,
}: {
  imageCount: number;
  totalActivities: number;
  activeAppointmentCount: number;
}) {
  const rows: { label: string; note?: string }[] = [];
  if (imageCount > 0) rows.push({ label: `${imageCount} ảnh & video` });
  if (totalActivities > 0)
    rows.push({ label: `${totalActivities} hoạt động trong lịch sử` });
  if (activeAppointmentCount > 0)
    rows.push({
      label: `${activeAppointmentCount} lịch hẹn xem nhà liên quan`,
      note: "sẽ huỷ",
    });

  return (
    <div className="rounded-xl border border-rose-200/70 bg-rose-50/60 px-3 py-2.5">
      <div className="mb-1.5 font-mono text-[11px] font-bold tracking-[0.08em] text-rose-700/80 uppercase">
        Sẽ mất
      </div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-ink-faint">
          Không có dữ liệu liên quan.
        </div>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((row) => (
            <li
              key={row.label}
              className="flex items-center justify-between gap-3 text-[12px] text-ink-soft"
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className="inline-block size-1.5 shrink-0 rounded-full bg-rose-400"
                />
                {row.label}
              </span>
              {row.note ? (
                <span className="shrink-0 text-[11px] font-medium text-rose-600">
                  {row.note}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
